/**
 * QUIL — IndexedDB SCHEMA (Dexie)
 * Source of truth: Quil-Build.md §8 (rebalanced 2026-05-14)
 *
 * Key decisions documented here:
 * - Works, Chapters, Characters, Pairings, PlotPoints, CanonNotes
 *   are all writer-created data stored in IndexedDB AND synced to OneDrive.
 * - Shape and TropeBeats are BUILT-IN data bundled with the app (see src/data/).
 *   They are NOT stored in Dexie.
 * - The sync queue is stored in Dexie so it survives browser restarts.
 * - Time-on-task data is per-session, per-device only — NOT persisted (FR-WRITE-38).
 *
 * Dexie index notation:
 *   ++id   = auto-increment primary key
 *   &field = unique index
 *   field  = non-unique index
 *   [a+b]  = compound index
 */

import Dexie from 'dexie'
import type { DexieOptions, Table } from 'dexie'
import { uuid } from './uuid'
import { countProseWords } from './text'
import type { ConflictRecord } from './syncConflict'
import type { RecoveryKind } from './recovery'
import { parseOwnerStamp } from './storageScope'
import { quotaMiddleware } from './localWriteFailure'
import type { OwnerStamp } from './storageScope'

// ─── Entity types ────────────────────────────────────────────────────────────

export type ChapterStatus =
  | 'not-started'
  | 'in-progress'
  | 'proofing'
  | 'completed'

export type PairingType = 'romantic' | 'platonic'
// romantic → AO3 '/' separator; platonic → AO3 '&' separator

export type SyncStatus = 'synced' | 'syncing' | 'pending' | 'conflict' | 'error'

type SyncOperation = 'upload' | 'delete'

// ─── Chapter ─────────────────────────────────────────────────────────────────

export interface Chapter {
  id: string           // UUID
  workId: string
  order: number        // 0-based display order within the Work
  title: string
  prose: string        // TipTap JSON (JSON.stringify of editor.getJSON()). The chapter content.
  status: ChapterStatus
  // Planning fields
  tropeBeatRefs: Array<{        // which trope beats are "engaged" in this chapter
    tropeId: string
    beatId: string
  }>
  // Chapter metadata
  // Per-chapter word-count target (overrides the work-level default / the computed
  // targetWordCount÷targetChapterCount). Additive, non-indexed — no bump.
  wordGoal?: number
  hook?: string                 // first-line / chapter hook
  // Writing surface metadata
  aNoteTop?: string             // Author's Note above chapter
  aNoteBottom?: string          // Author's Note below chapter
  // AO3 posting state (publishing-workflow §1). Orthogonal to `status` (which is
  // WRITING state — a posted chapter can still be `proofing` after an edit round),
  // so these are separate optional fields, NOT a fifth ChapterStatus. Both ride in
  // the synced chapter meta (serialized by saveChapterMeta), additive + non-indexed
  // → no Dexie bump. Absence of postedAt = not posted.
  postedAt?: number             // epoch ms when marked posted to AO3
  ao3Url?: string               // optional URL of the posted chapter on AO3
  // Friendly label of the device that last wrote this chapter (device-names feature).
  // Rides the synced meta so another device can say "edited on Desk" in a conflict.
  lastEditedDevice?: string
  // Home-page stat, recomputed on every save and carried in the synced meta.json,
  // so the Dashboard can total a work's words WITHOUT downloading any prose.
  wordCount?: number
  // Sync fields
  cTag?: string                 // the prose file's OneDrive eTag (the name predates W10): its If-Match
  // The prose file's content tag (cTag) as last written or read here. Device-local, never
  // synced. The eTag changes when a work folder is moved or renamed; the cTag does not
  // (W11 live test 22), so this is how a recreated write recognises the moved original as
  // the version this device last saw (sync check-moved). Additive, non-indexed.
  proseCTag?: string
  // Device-local cache flag — NEVER synced (stripped from meta.json). `false` means
  // this chapter's prose lives in OneDrive but hasn't been downloaded to this device
  // yet: fetch it (ensureChapterProse) before showing/editing. Set true on any local
  // save and after a successful on-open fetch. Prose is lazy so cold start needn't
  // pull every story body; undefined is treated as loaded (pre-v8 / locally authored).
  proseLoaded?: boolean
  // Device-local dirty marker — NEVER synced (stripped from meta.json, like proseLoaded/
  // cTag). Set by the crash-insurance flush (flushChapterProse) in the SAME raw IDB
  // transaction that writes the prose, so a page torn down between the flush and the
  // follow-up saveChapter leaves a durable "this body still needs uploading" record.
  // A normal saveChapter clears it (it enqueues the upload); a launch-time sweep
  // (sweepCrashFlushedProse) converts any surviving marker into a real queue entry.
  // While set, the down-sync prose guard treats the chapter as pending (won't
  // invalidate the un-uploaded local prose — the eventual upload records a conflict). (D2)
  needsUpload?: boolean
  createdAt: number
  updatedAt: number
}

// ─── Scene ───────────────────────────────────────────────────────────────────
// A sub-unit of a Chapter (§7, S4). A chapter is a collection of scenes; a scene
// exists to advance one or more plot points, transition between them, or bulk up
// the story (a scene may cover ZERO plot points — connective tissue). Its purpose
// IS the plot points it covers, so there is no separate Vonnegut "goal" field:
// membership lives on PlotPoint.sceneId (mirroring the older chapterId pattern).
// The chapter's prose stays ONE continuous document — scenes are soft navigation
// (auto-anchored into the prose while writing), never hard `---` breaks (item 2.8).
// Synced as an aggregate file (scenes.json), like characters/pairings/plotpoints.

export interface Scene {
  id: string           // UUID
  workId: string
  chapterId: string    // the chapter this scene belongs to
  order: number        // 0-based order within the chapter
  title?: string       // optional short label (e.g. "the morning after"); plot
                       // points are the primary identity in the navigator
  characterIds: string[]  // characters present in this scene (chip picker, 4b)
  // Sync fields
  cTag?: string
  createdAt: number
  updatedAt: number
}

// ─── CanonNote ───────────────────────────────────────────────────────────────
// One atomic fact per record. Surfaced one-at-a-time during writing (FR-REF-5).

export interface CanonNote {
  id: string
  characterId: string
  workId: string
  fact: string        // one canon fact, freetext
  createdAt: number
  updatedAt: number
}

// ─── Character ───────────────────────────────────────────────────────────────

export interface Character {
  id: string
  workId: string
  name: string
  gender?: 'male' | 'female' | 'nonbinary' | 'other'
  aliases: string[]
  canvasX?: number   // character whiteboard position
  canvasY?: number
  boardColor?: 'red' | 'blue' | 'yellow' | 'green' | 'purple' | 'pink'
  ao3TagString?: string     // canonical AO3 tag for this character
  isMajor: boolean
  // Snowflake step 3 fields (one-pager)
  onePager?: {
    motivation?: string     // what they want
    goal?: string           // their concrete goal in this story
    conflict?: string       // their main obstacle
    epiphany?: string       // what they learn/realise
  }
  voiceScratch?: string     // voice scratchpad (F30), freetext markdown
  fullSynopsis?: string    // Snowflake step 5 — character narrative synthesis
  fullChart?: string       // Snowflake step 7 — full character bible
  notes?: string
  // Sync fields
  cTag?: string
  createdAt: number
  updatedAt: number
}

// ─── Pairing ─────────────────────────────────────────────────────────────────

export interface Pairing {
  id: string
  workId: string
  characterIds: string[]    // 1..N; order determines AO3 tag display order
  type: PairingType
  displayName?: string      // optional fandom shorthand; falls back to computed
  // Computed fallback (never stored): characterIds joined by '/' (romantic)
  // or '&' (platonic) using each character's ao3TagString or name
  // Freeform relationship arc (where they start → what changes → where they land).
  // Additive, non-indexed — no bump. Shown in the edge menu + ReferencePane.
  arc?: string
  // Sync fields
  cTag?: string
  createdAt: number
  updatedAt: number
}

// ─── PlotPoint ───────────────────────────────────────────────────────────────

export interface PlotPoint {
  id: string
  workId: string
  title: string
  description?: string
  // Shape beat reference
  shapeBeatId?: string      // references built-in Shape.beats[].id
  // Character association
  aboutCharacterIds: string[]
  // Positioning
  order?: number            // ordering within the plot-point list / its scene lane (wizard dnd)
  chapterId?: string        // which chapter this plot point is assigned to (denormalised;
                            // when sceneId is set it mirrors the scene's chapterId)
  sceneId?: string          // which scene (within the chapter) this plot point is covered in (§7, S4)
  covered?: boolean         // ticked off in the chapter's "to cover" checklist while writing
  // Plot-line graph (P3-9 React Flow plot board). All additive/non-indexed — no bump.
  lineIds?: string[]        // plot lines (Work.plotLines[].id) this point belongs to (0..N)
  nextIds?: string[]        // directed connectors: the points that come AFTER this one on
                            // the shared line(s); per-line order = topological sort of these
  canvasX?: number          // plot-board position
  canvasY?: number
  // Sync fields
  cTag?: string
  createdAt: number
  updatedAt: number
}

// ─── Work ────────────────────────────────────────────────────────────────────

export interface Work {
  id: string
  title: string
  summary?: string
  fandom?: string           // primary fandom tag
  rating?: string           // AO3 rating category
  status: 'ongoing' | 'complete' | 'hiatus'
  // Planning
  shapeId?: string          // references built-in Shape.id
  // Tropes active on this Work (refs to built-in TropeBeats)
  activeTropeIds: string[]
  // Shape-segment mapping (S7/4.4): beatId → 0-based start chapter index. Lays the
  // Shape's beats over the chapters as contiguous segments; authored by dragging
  // dividers on the Canvas Chapters page, read by the Writing-Surface scale (2.6).
  // Additive, non-indexed (no migration); absent/partial → defaults from beat
  // positions. Interpreted via src/lib/shapeSegments.ts.
  shapeSegments?: Record<string, number>
  // Plot lines (P3-9): named + coloured threads a plot point can belong to (a point may
  // be on several). Ordering along a line is derived from the plot-point connectors
  // (PlotPoint.nextIds), numbered by topological sort — not stored here. A line with a
  // `pairingId` is a **relationship plot** (P3-10) — its points are that relationship's
  // story beats; it renders as a relationship lane on the timeline. Additive,
  // non-indexed (no Dexie bump), like shapeSegments.
  plotLines?: Array<{ id: string; name: string; color: string; pairingId?: string }>
  // Writing goals
  targetWordCount?: number
  targetChapterCount?: number    // planned number of chapters (§7, S4: Parts/Books removed)
  // Unified planning-wizard progress (replaces the retired snowflakeState).
  wizardState?: {
    completedSteps: number[]   // 1-based step numbers the writer has finished
    lastStep?: number          // where to resume the wizard
    done?: boolean             // walked the whole wizard at least once
  }
  authorPseud?: string      // per-work pseudonym (FR-WIP-F29)
  // Export metadata (Phase 8) — persisted so tags survive between sessions
  exportFandoms?: string[]
  exportRating?: string
  exportWarnings?: string[]
  exportCategory?: string[]
  exportFreeformTags?: string[]
  // Per-work Author's-Note templates (publishing-workflow §4). One skeleton each
  // for the top/bottom note; a chapter's "Insert template" fills an empty field
  // (appends if non-empty). Markdown-lite (renderAnMarkdown) like the notes
  // themselves. Additive, non-indexed — no Dexie bump. Templates follow the work,
  // not the device/library (settings are device-local in Quil).
  anTemplateTop?: string
  anTemplateBottom?: string
  // Explicit AO3 relationship-tag order (publishing-workflow §5): pairing ids in
  // export order (primary ship first). Export emits listed pairings in this order,
  // then any unlisted (newer) ones. Additive, non-indexed — no Dexie bump.
  relationshipOrder?: string[]
  // Trope beats the writer has ticked as "planned" in the wizard (FR-PLAN-9). Keys
  // are `${tropeId}:${beatId}`. A lightweight planning intent, independent of the
  // chapter-specific TimelineBoard placement (Chapter.tropeBeatRefs). Additive,
  // non-indexed — no Dexie bump.
  plannedTropeBeats?: string[]
  // Writer's own note appended under a Shape beat's canonical text (FR-PLAN-5),
  // keyed by beatId. e.g. { 'stc-catalyst': 'the letter arrives' }. Additive,
  // non-indexed — no bump.
  beatNotes?: Record<string, string>
  // Per-work scene-break glyph shown in the editor and mapped on export. Undefined/
  // empty → the default `<hr>`; a glyph (e.g. "✦", "⁂", "—") → a centred paragraph
  // on export (AO3-safe). Additive, non-indexed — no Dexie bump.
  sceneBreakGlyph?: string
  // Default per-chapter word goal for NEW chapters in this work ("use as default").
  // The effective goal is chapter.wordGoal ?? this ?? targetWordCount÷targetChapterCount.
  // Additive, non-indexed — no bump.
  defaultChapterWordGoal?: number
  // Chosen Save the Cat genre overlay (one of Shape.genreOverlays), e.g. "Buddy
  // Love" — a genre lens the writer reads the 15 beats through. A label only (no
  // per-genre beat text is bundled). Additive, non-indexed — no Dexie bump.
  genreOverlay?: string
  // Sync fields
  cTag?: string
  createdAt: number
  updatedAt: number
}

// ─── Sync queue entry ────────────────────────────────────────────────────────
// Persisted in IndexedDB so the queue survives browser restarts (FR-SYNC-13).

export interface SyncQueueEntry {
  id?: number              // auto-increment
  entityType: 'work' | 'chapter' | 'scene' | 'character' | 'pairing' | 'plotpoint' | 'canonnote' | 'cut' | 'tags'
  entityId: string
  workId: string
  operation: SyncOperation
  remotePath: string       // OneDrive path, e.g. /Quil/works/{workId}/meta.json
  payload?: string         // JSON string to upload (undefined for deletes)
  retryCount: number
  nextRetryAt: number      // epoch ms; 0 = ready immediately
  errorMessage?: string
  createdAt: number
  // W11 conditional delete: the eTag of the version this device last saw, sent as the
  // DELETE's If-Match so a delete never removes an edit made on another device since
  // (decision 2, 2026-10-07). Non-indexed, additive. Absent on uploads, and on a delete
  // of a file this device never saw (a lazy chapter body never opened here).
  ifMatch?: string
  // W11: a delete of a chapter body this device never downloaded (lazy prose, never
  // opened here), so it holds no version it could have seen. The drain deletes against
  // the eTag it reads at that moment (sync conflict paths, pushChapterDelete).
  unseen?: true
}

// ─── Tombstone ────────────────────────────────────────────────────────────────
// Aggregate entities (character/pairing/plotpoint/canonnote) are stored as one
// JSON file per type per work. A bare "live entities" array can't express a
// deletion: an absent id is ambiguous (deleted on another device, or added here
// and not yet uploaded). Tombstones record deletions with a timestamp so the
// per-entity merge (sync.ts) propagates deletes without resurrecting un-uploaded
// additions. Local-only; written into the aggregate file on upload.

export type AggregateEntityType = 'character' | 'pairing' | 'plotpoint' | 'canonnote' | 'scene' | 'cut'

export interface Tombstone {
  entityId: string            // the deleted entity's id (primary key)
  entityType: AggregateEntityType
  workId: string
  deletedAt: number
}

// ─── Prose conflict (LEGACY, v3-v10) ────────────────────────────────────────────
// The pre-W11 row shape, kept only so the v10 upgrade can read it. Recorded when a prose upload is rejected (HTTP 412 via If-Match) because the
// chapter changed on another device since this device last synced. BOTH versions
// are preserved until the writer resolves the conflict — never a silent
// last-writer-wins (NFR-20). At most one open conflict per chapter.

interface ProseConflict {
  chapterId: string    // primary key — references Chapter.id
  workId: string
  chapterTitle: string
  localProse: string   // this device's prose (TipTap JSON) at the time of the rejected upload
  remoteProse: string  // the other device's prose, pulled from OneDrive
  remoteCTag: string   // OneDrive eTag of the remote version (used as If-Match when keeping local)
  remoteDevice?: string // friendly label of the device that wrote the remote version (best-effort)
  detectedAt: number
}

// ─── Sync conflict record (W11) ─────────────────────────────────────────────────
// Since W11 (app-platform design/W11-sync-orchestration.md) a conflict is the shared
// `ConflictRecord` (lib/syncConflict.ts): both versions, both tags, the cause, keyed
// `${unitKind}:${id}` so there is one open record per unit. Quil adds `workId` so a
// work's state, a work delete and a remote work delete can find its records by index.
// The v10 upgrade carries every v3 `ProseConflict` row across (db.migrations.test.ts).
// Kept losers would also live here (`resolved` set), but Quil keeps its losers as
// 'pre-conflict' snapshots instead, so in practice every row is an open record.

export interface QuilConflict extends ConflictRecord<string> {
  workId: string
}

// ─── Remote tag (W11) ───────────────────────────────────────────────────────────
// The eTag of the version of a meta or aggregate file this device last wrote or merged,
// so the next write of that file is `If-Match` it (ledger #15: they were unconditional).
// Device-local, never synced. A path with no row is read before its first write.

export interface RemoteTag {
  path: string   // the logical /Apps/Quil/... path
  eTag: string
}

// ─── Snapshot ──────────────────────────────────────────────────────────────────
// Draft-safety layer 1 (brief: draft-safety). A point-in-time copy of a chapter's
// prose, taken automatically before a risky overwrite or manually from the WS menu.
// DEVICE-LOCAL — never synced (cross-device disaster recovery is OneDrive versions'
// job, layer 2). Do NOT add to storage.ts's enqueue paths. Uses wall-clock (Date.now)
// for takenAt because it's local-only and drives a relative-time display.

export type SnapshotReason =
  | 'manual'            // WS-menu "Snapshot" (optional label)
  | 'pre-conflict'      // ConflictDialog, before either resolution
  | 'pre-remote-adopt'  // ensureChapterProse, before overwriting non-empty local prose
  | 'pre-restore'       // before a snapshot / OneDrive-version restore
  | 'pre-replace'       // before a batch find-replace

export interface Snapshot {
  id: string           // UUID
  workId: string
  chapterId: string
  prose: string        // TipTap JSON at capture time
  wordCount: number
  takenAt: number      // wall-clock ms (device-local, never synced)
  reason: SnapshotReason
  label?: string       // optional writer-supplied label (manual) or system note ("remote version")
}

// ─── Trash entry ────────────────────────────────────────────────────────────────
// Draft-safety layer 3 — the recovery shelf. Captures a full entity graph BEFORE a
// work/chapter delete cascade runs, so a confirmed deletion is still recoverable.
// DEVICE-LOCAL — never synced (same rationale as Snapshot). Purged after 30 days.

export interface TrashEntry {
  id: string           // UUID (independent of the deleted entity's id); the key of an entry written since W12 Phase B
  deletedAt: number    // wall-clock ms
  /** 'prose' (W12 Phase B): a kept version of a chapter's text (a conflict loser), not a deleted unit. */
  kind: 'work' | 'chapter' | 'prose'
  // Full serialized entity graph captured before the cascade. For a work: the work
  // + its chapters (incl. prose) + scenes + characters + pairings + plotPoints +
  // canonNotes. For a chapter: the chapter (incl. prose) + its scenes. Empty for 'prose'.
  payload: TrashPayload
  /**
   * W12 Phase B (additive, non-indexed: no Dexie bump). `trash` IS quil's recovery table: the shared
   * `recovery.ts` rules run over it through `trashStore` (lib/trash.ts). A row written by a delete
   * (storage.ts) has none of these and reads as a `deleted` entry kept 30 days from `deletedAt`;
   * a row written since carries them.
   */
  recovery?: TrashRecovery
  /** `kind: 'prose'` only: the kept text and where it belongs. */
  prose?: { workId: string; chapterId: string; text: string }
}

/** The recovery fields a trash row carries beyond the legacy shape (`RecoveryEntry` minus its value and key). */
export interface TrashRecovery {
  kind: RecoveryKind
  unitKind: string
  unitId: string
  label: string
  /** ISO time after which it is purged; null = manual, kept. */
  keepUntil: string | null
  deviceId: string
  bytes?: number
}

export interface TrashPayload {
  work?: Work
  chapters?: Chapter[]
  scenes?: Scene[]
  characters?: Character[]
  pairings?: Pairing[]
  plotPoints?: PlotPoint[]
  canonNotes?: CanonNote[]
  /** The work's cuts (the graveyard), kept with it so a restore brings them back. Absent on entries
   *  written before 2026-10-10: those restore without cuts, as they always did. */
  cuts?: Cut[]
}

// ─── Cut ────────────────────────────────────────────────────────────────────────
// Draft-safety layer 4 — the graveyard. A passage deliberately cut from a chapter's
// prose but kept in case the writer wants it back. UNLIKE snapshots/trash, cuts ARE
// synced (they're library content) — as a per-work aggregate file (cuts.json), using
// the exact CanonNote aggregate pattern: storage's enqueueAggregateSync + sync.ts's
// downSyncAggregate + the `tombstones` table (entityType 'cut'). (The brief mentioned
// a `deleted` flag, but the real aggregate machinery uses the tombstones table, so
// cuts follow that — see briefs/draft-safety.md, updated to match.)

export interface Cut {
  id: string            // UUID
  workId: string
  chapterId?: string    // origin chapter (may be gone later — kept for display only)
  chapterTitle?: string // origin chapter title snapshot (for display)
  content: string       // TipTap fragment JSON (the cut slice) — re-insertable
  textPreview: string   // plain-text preview
  createdAt: number
  updatedAt: number
}

// ─── App settings ─────────────────────────────────────────────────────────────

// 'auto' follows the OS: light in a light system, the user's chosen dark variant
// (autoDarkVariant) in a dark system. Resolved to a concrete theme at apply time.
export type Theme = 'light' | 'dark' | 'oled' | 'auto'
// The 5 runtime-overridable type roles (Bright design system).
export type FontRole = 'display' | 'heading' | 'read' | 'ui' | 'label'

// A reading font dropped into OneDrive /Quil/fonts/ — the only legitimate path
// to Bookerly (licensing-locked). Registered as a FontFace at runtime.
export interface CustomFont {
  family: string      // the @font-face family name we register + apply
  fileName: string    // the file inside the OneDrive Quil/fonts/ folder
}

export interface AppSettings {
  id: 1  // singleton — only one row
  lastOpenedWorkId?: string
  lastOpenedChapterId?: string
  oneDriveConnected: boolean
  oneDriveUserId?: string
  deltaToken?: string       // OneDrive delta query token (FR-SYNC-11)
  // OneDrive-first (Phase 2): set true once this device has completed its first
  // gated sign-in reconcile (down-sync merge + push local-only up). Gates the
  // cold-start barrier — absent/false ⇒ show the retrieving-the-library gate once.
  // Non-indexed, additive (no Dexie version bump), like shapeSegments.
  initialSyncComplete?: boolean
  // ── Personalisation (Bright design system) ──
  theme: Theme              // Light / Dark / OLED / Auto
  // Which dark variant 'auto' uses at night — set whenever the writer explicitly
  // picks a dark theme, so Auto follows "your chosen dark variant". Non-indexed,
  // additive (no version bump), like the other personalisation fields.
  autoDarkVariant?: 'dark' | 'oled'
  accent: string            // hex — re-tints the whole UI via color-mix(oklch)
  fonts?: Partial<Record<FontRole, string>>  // per-role font-family CSS override
  // Fonts discovered in the OneDrive /Apps/Quil/fonts/ folder — auto-registered on
  // load and offered in every font picker. Non-indexed, additive (no version bump).
  customFonts?: CustomFont[]
  // Smart typography (opt-in): while writing, convert `--`→— , `...`→… and straight
  // quotes → curly. Off by default (so the `---` scene-break input rule keeps
  // working — `--` would consume the third dash). Non-indexed, additive (no bump).
  smartTypography?: boolean
  // Typewriter scrolling (opt-in): keep the caret line vertically centred in the
  // writing surface while typing. Off by default. Non-indexed, additive (no bump).
  typewriterScroll?: boolean
  // Page & type comfort (opt-in overrides; token-applied to the editor only):
  //   readingMeasure   — text column width in `ch` (default 56)
  //   readingLineHeight — unitless line-height (default 1.75)
  //   paragraphStyle   — 'spaced' (gap between paragraphs, default) | 'indented'
  // Additive, non-indexed — no bump.
  readingMeasure?: number
  readingLineHeight?: number
  paragraphStyle?: 'spaced' | 'indented'
  // Friendly label for THIS device ("Desk", "iPad"). Local-only (each device names
  // itself); stamped onto synced writes as Chapter.lastEditedDevice so OTHER devices
  // can say "edited on Desk" in conflicts. Non-indexed, additive (no bump).
  deviceName?: string
  // Server-anchored clock (lib/clock.ts): this device's learned offset from
  // OneDrive's clock, applied to every synced entity timestamp so cross-device
  // LWW is skew-immune. Non-indexed, additive (no version bump).
  clockOffsetMs?: number
  // Manual reduce-motion opt-in (F6, ported from Inkling): force reduced motion
  // (no transitions/animations, instant loading-gate ticker) regardless of the OS
  // setting — for a device whose OS setting isn't the user's to change. The OS
  // `prefers-reduced-motion` still applies on top; this just adds an in-app opt-in.
  // Off by default. Non-indexed, additive (no version bump).
  reduceMotion?: boolean
}

// ─── Retired fields (v13) ─────────────────────────────────────────────────────

/**
 * Fields of cut features, per table: the v13 upgrade deletes them from every stored row, and
 * down-sync strips them from a file another device wrote before v13, so they never come back.
 * One list for both. Never add a field here without a version bump that strips it.
 */
const RETIRED_FIELDS = {
  works: ['cadenceTarget', 'cutsDocument'],
  chapters: ['shapeBeatId', 'goal', 'linesAdvanced', 'cedCharacterIds', 'povCharacterId', 'aboveLine', 'emotionalBeats'],
  plotPoints: ['tropeBeatRef', 'aboutPairingId', 'aboveLine', 'isEmotionalBeat'],
  scenes: ['notes'],
  pairings: ['notes'],
  settings: ['customReadingFont'],
} as const satisfies Record<string, readonly string[]>

/** A copy of `row` without the retired fields of the Dexie table `table` (the row is not changed). */
export function withoutRetired<T extends object>(table: string, row: T): T {
  const fields: readonly string[] = (RETIRED_FIELDS as Record<string, readonly string[]>)[table] ?? []
  if (!fields.some((f) => f in row)) return row
  const copy = { ...row } as Record<string, unknown>
  for (const f of fields) delete copy[f]
  return copy as T
}

// ─── Dexie database class ─────────────────────────────────────────────────────

/** The pre-W12 database name, and the base every account's scoped name is built on (W12 A5). */
const BASE_DB_NAME = 'QuilDatabase'
/** The Dexie schema version this build writes (Dexie keeps `version * 10` as the IndexedDB version). */
export const SCHEMA_VERSION = 13

export class QuilDatabase extends Dexie {
  works!:       Table<Work, string>
  chapters!:    Table<Chapter, string>
  scenes!:      Table<Scene, string>
  characters!:  Table<Character, string>
  pairings!:    Table<Pairing, string>
  plotPoints!:  Table<PlotPoint, string>
  canonNotes!:  Table<CanonNote, string>
  syncQueue!:   Table<SyncQueueEntry, number>
  settings!:    Table<AppSettings, number>
  syncConflicts!: Table<QuilConflict, string>
  remoteTags!:  Table<RemoteTag, string>
  tombstones!:  Table<Tombstone, string>
  snapshots!:   Table<Snapshot, string>
  trash!:       Table<TrashEntry, string>
  cuts!:        Table<Cut, string>
  /**
   * One row per key. Today only `{ key: 'owner', accountId, at, proof }`: the W12 owner stamp. It is
   * the commit point of adopting a store for an account, and from v12 on the ONLY thing that says
   * whose store this is (`settings.oneDriveUserId` is the pre-W12 residue the v12 upgrade stamps from).
   */
  meta!:        Table<OwnerStamp, string>

  constructor(name = BASE_DB_NAME, options?: DexieOptions) {
    super(name, options)

    this.version(1).stores({
      works:       '&id, status, updatedAt',
      chapters:    '&id, workId, order, status, updatedAt, [workId+order]',
      marks:       '&id, chapterId, workId, category',   // dropped in v6 (was never written to)
      characters:  '&id, workId, isMajor, updatedAt',
      pairings:    '&id, workId, type',
      plotPoints:  '&id, workId, chapterId, shapeBeatId',
      canonNotes:  '&id, characterId, workId',
      sessions:    '&id, workId, chapterId, startedAt',
      syncQueue:   '++id, entityType, entityId, nextRetryAt, operation',
      settings:    '&id',
    })
    // v2: add workId index to syncQueue so deleteWork can clear the queue transactionally
    this.version(2).stores({
      syncQueue: '++id, entityType, entityId, workId, nextRetryAt, operation',
    })
    // v3: prose conflict store (If-Match 412 detection). Additive — no data transform.
    this.version(3).stores({
      conflicts: '&chapterId, workId',
    })
    // v4: tombstones for aggregate-entity deletion propagation. Additive.
    this.version(4).stores({
      tombstones: '&entityId, [workId+entityType], workId',
    })
    // v5: Scene entity (§7, S4) — a sub-unit of Chapter. Adds the scenes table
    // and a sceneId index on plotPoints. Migration is additive but transforms
    // data: every existing chapter gets a default Scene, and that chapter's plot
    // points are reassigned to it (chapterId → the new scene). Order is preserved.
    this.version(5).stores({
      scenes:     '&id, workId, chapterId, [chapterId+order]',
      plotPoints: '&id, workId, chapterId, shapeBeatId, sceneId',
    }).upgrade(async (tx) => {
      const now = Date.now()
      const chapters = await tx.table('chapters').toArray()
      const plotPoints = await tx.table('plotPoints').toArray()
      const ppByChapter = new Map<string, typeof plotPoints>()
      for (const pp of plotPoints) {
        if (!pp.chapterId) continue
        const list = ppByChapter.get(pp.chapterId) ?? []
        list.push(pp)
        ppByChapter.set(pp.chapterId, list)
      }
      for (const ch of chapters) {
        const scene: Scene = {
          id: uuid(),
          workId: ch.workId,
          chapterId: ch.id,
          order: 0,
          characterIds: [],
          createdAt: now,
          updatedAt: now,
        }
        await tx.table('scenes').put(scene)
        for (const pp of ppByChapter.get(ch.id) ?? []) {
          await tx.table('plotPoints').update(pp.id, { sceneId: scene.id })
        }
      }
    })
    // v6: drop the vestigial `marks` table (Marks are TipTap highlight spans in the
    // prose JSON, never records — the table was created in v1 but never written to,
    // so this is a no-op for data). Phase 3 cleanup.
    this.version(6).stores({
      marks: null,
    })
    // v7: chapter status model → not-started / in-progress / proofing / completed
    // (P3-16/P3-17). Remap legacy values so existing chapters display correctly.
    // Schema (the `status` index) is unchanged; this only rewrites values.
    this.version(7).upgrade(async (tx) => {
      const legacyMap: Record<string, ChapterStatus> = {
        resting: 'in-progress',
        'ready-to-post': 'proofing',
        'needs-revision': 'proofing',
        posted: 'completed',
      }
      const chapters = await tx.table('chapters').toArray()
      for (const ch of chapters) {
        // 'draft' → in-progress if it has prose, else not-started; others by the map.
        const next = ch.status === 'draft'
          ? (ch.prose && ch.prose.length > 2 ? 'in-progress' : 'not-started')
          : (legacyMap[ch.status as string] ?? 'not-started')
        if (next !== ch.status) await tx.table('chapters').update(ch.id, { status: next })
      }
    })
    // v8: lazy prose. Backfill `wordCount` (so the Dashboard can total words without
    // loading prose) and mark every existing chapter `proseLoaded: true` — their prose
    // is already on this device. Additive; no data is dropped.
    this.version(8).upgrade(async (tx) => {
      const chapters = await tx.table('chapters').toArray()
      for (const ch of chapters) {
        await tx.table('chapters').update(ch.id, {
          wordCount: countProseWords(ch.prose ?? ''),
          proseLoaded: true,
        })
      }
    })
    // v9: draft-safety cluster (brief: draft-safety). Three new stores, all additive
    // (no data transform). `snapshots` + `trash` are DEVICE-LOCAL (never synced);
    // `cuts` IS synced as a per-work aggregate. Declared together in one bump so the
    // whole cluster shares a single schema version. See the type defs above.
    this.version(9).stores({
      snapshots: '&id, chapterId, workId, takenAt',
      trash:     '&id, deletedAt',
      cuts:      '&id, workId, createdAt',
    })
    // v10: W11 sync orchestration. `syncConflicts` holds the shared ConflictRecord (one
    // open record per unit, keyed `${unitKind}:${id}`) and replaces v3's `conflicts`,
    // whose primary key (chapterId) Dexie cannot change in place; every open prose
    // conflict is carried across. `remoteTags` holds the last-seen eTag of each meta and
    // aggregate file, so those writes can be If-Match (ledger #15).
    this.version(10).stores({
      syncConflicts: '&key, workId, id',
      remoteTags:    '&path',
    }).upgrade(async (tx) => {
      const old = (await tx.table('conflicts').toArray()) as ProseConflict[]
      for (const c of old) {
        const row: QuilConflict = {
          key: `prose:${c.chapterId}`,
          unitKind: 'prose',
          id: c.chapterId,
          workId: c.workId,
          // QUIL_ONEDRIVE_ROOT ('/Apps/Quil'); db.ts must not import auth.ts (MSAL).
          path: `/Apps/Quil/works/${c.workId}/chapters/${c.chapterId}.prose.json`,
          label: c.chapterTitle || 'Untitled chapter',
          cause: 'changed',
          local: { value: c.localProse, tag: null, modifiedAt: null },
          remote: {
            value: c.remoteProse,
            tag: c.remoteCTag || null,
            eTag: c.remoteCTag || null,
            modifiedAt: null,
            ...(c.remoteDevice ? { device: c.remoteDevice } : {}),
          },
          detectedAt: new Date(c.detectedAt || 0).toISOString(),
          deviceId: 'this-device',
        }
        await tx.table('syncConflicts').put(row)
      }
    })
    // v11: drop v3's `conflicts`, now empty of meaning (v10 carried its rows across). A
    // separate version so the drop can never run before the copy.
    this.version(11).stores({
      conflicts: null,
    })
    // v12 (W12 step A5, app-platform design/W12-storage.md section 3.1): the `meta` table that holds
    // the owner stamp. Additive. THE STAMP COMES FROM `settings.oneDriveUserId` in the same upgrade
    // (P1, the most certain adoption in the estate: every pre-W12 sign-in wrote it), so the stamp
    // and the version change commit together or not at all; a killed upgrade leaves v11 and no
    // stamp, and the ladder simply runs again. A store whose `oneDriveUserId` was never set (never
    // signed in) is left unstamped: its owner is unknown and the ladder asks (quilScope.ts).
    this.version(12).stores({
      meta: 'key',
    }).upgrade(async (tx) => {
      const settings = (await tx.table('settings').get(1)) as AppSettings | undefined
      const accountId = settings?.oneDriveUserId
      if (typeof accountId === 'string' && accountId.length > 0) {
        const stamp: OwnerStamp = { key: 'owner', accountId, at: new Date().toISOString(), proof: 'owner-stamp' }
        await tx.table('meta').put(stamp)
      }
    })

    // v13 (2026-11-01, app-platform 8.2 reachability audit): drop what nine cut features left behind.
    // The `sessions` table (the session timer, cut in UI/UX pass 1) was never read or written, and
    // the fields in RETIRED_FIELDS belong to features that were cut (three-line plot model, emotional
    // beats, above/below-the-line, scene goal, posting cadence and Reader mode, the cuts document, the
    // manual reading font) or were never given a writer. Every row keeps everything else; down-sync
    // strips the same fields from files other devices wrote before this (withoutRetired, sync.ts).
    this.version(13).stores({
      sessions: null,
    }).upgrade(async (tx) => {
      for (const [table, fields] of Object.entries(RETIRED_FIELDS)) {
        await tx.table(table).toCollection().modify((row: Record<string, unknown>) => {
          for (const f of fields) delete row[f]
        })
      }
    })

    // The local-write quota rule (design 3.6): one middleware sees every write, reports a
    // QuotaExceededError (on the request, or on the transaction's abort) and rethrows it.
    this.use(quotaMiddleware)
  }
}

// ─── The database this tab is bound to (W12 A5) ────────────────────────────────
//
// Before W12 this file made ONE module-level `new QuilDatabase()` named `QuilDatabase`, whoever was
// signed in, and every reader and writer reached it by default. That is how a second account could
// have inherited the first one's library and drained its unsynced `syncQueue` into the second
// account's OneDrive (`oneDriveUserId` was simply overwritten).
//
// Now `db` is a stand-in with NOTHING behind it until `quilScope.resolveStore` has decided which
// store the signed-in account may open and `storeBinding` has called `setActiveDb`. Every module that
// reaches for `db` keeps working untouched, because the property is read at call time and the
// stand-in forwards to whichever database is bound then. Until one is, touching a table throws
// `NoStoreError`: nothing opens the legacy database "by accident" (which would upgrade it with no
// backup), the one property this design cannot do without. `src/test/setup.ts` binds a fresh
// database for the suite, since a test is one device with one account and no gate in front of it.

export class NoStoreError extends Error {
  constructor() {
    super('No local store is open yet: the signed-in account has not been matched to its library.')
    this.name = 'NoStoreError'
  }
}

let activeDb: QuilDatabase | null = null
const bindListeners = new Set<() => void>()

/** Bind (or, with `null`, unbind) the database this tab reads and writes. */
export function setActiveDb(database: QuilDatabase | null): void {
  activeDb = database
  for (const listener of [...bindListeners]) listener()
}

export function getActiveDb(): QuilDatabase | null {
  return activeDb
}

/** Told when a store is bound or unbound (the shell waits for it before it touches the data). */
export function subscribeActiveDb(listener: () => void): () => void {
  bindListeners.add(listener)
  return () => { bindListeners.delete(listener) }
}

function bound(): QuilDatabase {
  if (!activeDb) throw new NoStoreError()
  return activeDb
}

/**
 * The default every storage function reaches for. Forwards each property read to the bound database
 * (methods bound to it), and throws `NoStoreError` while none is bound.
 */
export const db: QuilDatabase = new Proxy({} as QuilDatabase, {
  get(_target, property) {
    // `then` and symbols are probes (an `await`, a debugger, a serialiser), not table access.
    if (!activeDb && (property === 'then' || typeof property === 'symbol')) return undefined
    const database = bound()
    const value: unknown = Reflect.get(database, property, database)
    return typeof value === 'function' ? (value as (...args: unknown[]) => unknown).bind(database) : value
  },
  has(_target, property) {
    return property in bound()
  },
})

/**
 * Whose store this is: the `meta` stamp, else (a store from before v12, or an unstamped one)
 * `settings.oneDriveUserId`, else null. The stamp wins whenever both exist.
 */
export async function readOwner(database: QuilDatabase): Promise<OwnerStamp | null> {
  const meta = (database as { meta?: { get(key: string): Promise<unknown> } }).meta
  const stamp = meta ? parseOwnerStamp(await meta.get('owner')) : null
  if (stamp) return stamp
  const settings = (await database.settings.get(1)) as AppSettings | undefined
  const id = settings?.oneDriveUserId
  return typeof id === 'string' && id.length > 0 ? { key: 'owner', accountId: id, at: '', proof: 'owner-stamp' } : null
}

/** The adoption's commit point: one single-row put. */
export async function putOwner(database: QuilDatabase, stamp: OwnerStamp): Promise<void> {
  await database.meta.put(stamp)
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Seed defaults — the data layer owns these; personalization.ts re-exports them for UI use. */
export const DEFAULT_ACCENT = '#093f24'   // forest — DESIGN.md v3 "Colours" §3: the pick is the GROUND, not the lit element
/* DARK, not light. DESIGN.md §3: "Dark is the primary theme. Light is the cream specimen sheet."
 * The seed said `light`, which nobody noticed because an existing install has its own saved theme
 * and only a brand-new device ever reads this. P4 made that visible: the landing page is deep
 * forest by contract, so a new device signed in and arrived in cream, which reads as two different
 * apps either side of one button. Kamrin's call, 2026-09-22. The contrast gate already covers both
 * themes, so nothing else moves. */
export const DEFAULT_THEME: Theme = 'dark'

/** Seed the settings row if it doesn't exist (first run). */
export async function ensureSettings(): Promise<AppSettings> {
  const existing = await db.settings.get(1)
  if (existing) return existing

  const defaults: AppSettings = {
    id: 1,
    oneDriveConnected: false,
    theme: DEFAULT_THEME,
    accent: DEFAULT_ACCENT,
  }
  await db.settings.put(defaults)
  return defaults
}
